import { useMemo, useState } from "react";
import {
  Building2,
  Sun,
  Battery,
  Gauge,
  Thermometer,
  HandCoins,
  Wallet,
  Calculator,
  ClipboardCheck,
  LineChart,
  Coins,
  ShieldAlert,
  type LucideIcon,
} from "lucide-react";

export type AnalysisModule = {
  id: string;
  category: string;
  title: string;
  description: string;
  benefit: string;
  chips: string[];
  example?: string;
  icon: LucideIcon;
  accent: "navy" | "leaf" | "orange";
};

const modules: AnalysisModule[] = [
  {
    id: "gebaeude",
    category: "Gebäude",
    title: "Gebäudezustand & Sanierungsbedarf",
    description:
      "Wir erfassen Hülle, Dämmung, Fenster, Lüftung, Heizsystem und energetischen Zustand strukturiert.",
    benefit: "Sie erkennen sofort, welche Maßnahmen sinnvoll, dringend oder förderfähig sind.",
    chips: ["Hülle", "Dämmung", "Fenster", "Lüftung", "Heizungsalter", "Sanierungsfahrplan"],
    example: "EFH Bj. 1985: Dämmung gut, Fenster mittel, Heizung austauschrelevant.",
    icon: Building2,
    accent: "navy",
  },
  {
    id: "pv",
    category: "PV & Speicher",
    title: "Photovoltaik & Dachpotenzial",
    description:
      "Wir prüfen Dachfläche, Ausrichtung, Neigung, Verschattung und Qualität möglicher PV-Komponenten.",
    benefit: "Realistische Ertragsbewertung statt reiner Werbeversprechen.",
    chips: ["Dachfläche", "Ausrichtung", "Verschattung", "Modulklasse", "Wechselrichter", "Garantien", "Degradation"],
    example: "Süddach 38 m²: ca. 8 kWp, rund 7.600 kWh Jahresertrag realistisch.",
    icon: Sun,
    accent: "orange",
  },
  {
    id: "speicher",
    category: "PV & Speicher",
    title: "Speicher, Wechselrichter & Systemfähigkeit",
    description:
      "Wir bewerten Speichergröße, Zyklen, Notstrom, AC-/DC-Kopplung und Erweiterbarkeit.",
    benefit: "Sie sehen, ob das System zu Ihrem Verbrauch und künftigen Erweiterungen passt.",
    chips: ["Speicherkapazität", "Zyklen", "Notstrom", "AC/DC", "Wärmepumpe", "E-Mobilität", "Erweiterbarkeit"],
    example: "8 kWh Speicher: Eigenverbrauch von ca. 32 % auf ca. 68 %.",
    icon: Battery,
    accent: "leaf",
  },
  {
    id: "ertrag",
    category: "PV & Speicher",
    title: "Real nutzbarer Energieertrag",
    description:
      "Wir berücksichtigen Systemverluste, Speicherwirkungsgrad, Leitungsverluste und Eigenverbrauchsquote.",
    benefit: "Wirtschaftlichkeit basiert auf real nutzbarer Energie, nicht auf Best-Case-Werten.",
    chips: ["MPP-Verluste", "Leitungsverluste", "Speicher-Wirkungsgrad", "Eigenverbrauch", "Systemverluste"],
    example: "9.000 kWh theoretisch ≈ 7.200 kWh real nutzbar.",
    icon: Gauge,
    accent: "navy",
  },
  {
    id: "wp",
    category: "Heizung",
    title: "Wärmepumpe & Heizsystem",
    description:
      "Wir prüfen Wärmebedarf, Vorlauftemperatur, Heizkörper, JAZ, Geräusche und Strombedarf.",
    benefit: "Sie erfahren, ob eine Wärmepumpe zu Ihrem Gebäude passt und welche Variante sinnvoll ist.",
    chips: ["Heizlast", "Vorlauftemperatur", "Heizkörper", "JAZ", "Speicher", "Geräusch", "Strombedarf"],
    example: "Vorlauf 45 °C: Luft-Wärmepumpe mit JAZ 3,8 realistisch.",
    icon: Thermometer,
    accent: "leaf",
  },
  {
    id: "foerder",
    category: "Förderung",
    title: "Fördermittel & Zuschüsse",
    description:
      "Wir prüfen KfW-, BAFA-, Landes- und kommunale Programme sowie mögliche Kombinationen.",
    benefit: "Sie vermeiden vergessene Fördermittel und finden die passende Förderung.",
    chips: ["KfW", "BAFA", "Land", "Kommune", "§35c EStG", "Kombinationen", "Tilgungszuschüsse"],
    example: "BEG EM + Landeszuschuss + KfW-Finanzierung sind je nach Projekt kombinierbar.",
    icon: HandCoins,
    accent: "leaf",
  },
  {
    id: "finanzierung",
    category: "Finanzierung",
    title: "Finanzierung & Monatsbelastung",
    description:
      "Wir vergleichen Förderkredite, Hausbank, Eigenmittel und mögliche Finanzierungsvarianten.",
    benefit: "Sie sehen, welche Lösung monatlich tragbar ist – mit und ohne Förderung.",
    chips: ["KfW-Kredit", "Hausbank", "Eigenmittel", "Tilgungszuschuss", "Liquidität", "Cashflow", "Monatsrate"],
    example: "KfW 270 vs. Hausbank: bis zu 84 € mtl. Differenz auf 10 Jahre.",
    icon: Wallet,
    accent: "navy",
  },
  {
    id: "steuer",
    category: "Steuern",
    title: "Steuern & Abschreibung",
    description:
      "Wir betrachten §35c EStG, 0 %-USt, IAB, Sonder-AfA und Vermietungsszenarien.",
    benefit: "Keine Standardrechnung – sondern eine steuerlich sinnvollere Projektbetrachtung.",
    chips: ["§35c EStG", "0 % USt PV", "IAB", "Sonder-AfA", "Vermietung", "AfA-Potenzial"],
    example: "§35c: 20 % Steuerbonus über 3 Jahre, max. 40.000 € je Objekt.",
    icon: Calculator,
    accent: "orange",
  },
  {
    id: "angebot",
    category: "Wirtschaftlichkeit",
    title: "Angebots- & Qualitätsprüfung",
    description:
      "Wir prüfen Angebote auf Preis, Leistungsumfang, Komponentenqualität, Garantien und Marktüblichkeit.",
    benefit: "Sie erkennen überhöhte Preise, fehlende Leistungen und technische Schwachstellen.",
    chips: ["Marktpreis", "Komponenten", "Garantien", "Leistungsumfang", "AGB", "Dimensionierung"],
    example: "PV-Angebot 1.890 €/kWp vs. marktüblich 1.450–1.650 €/kWp.",
    icon: ClipboardCheck,
    accent: "navy",
  },
  {
    id: "tco",
    category: "Wirtschaftlichkeit",
    title: "Wirtschaftlichkeit 10 / 20 / 30 Jahre",
    description:
      "Wir berechnen Kosten, Einsparungen, Wartung, Reinvest, Restwerte und Energiepreis-Szenarien langfristig.",
    benefit: "Sie sehen Payback, Rendite und Gesamteffekt über mehrere Zeiträume realistisch.",
    chips: ["TCO", "IRR", "Payback", "10 J.", "20 J.", "30 J.", "Sensitivität", "Restwert"],
    example: "PV + Speicher: IRR 6,8 %, Payback 11 J., 30-J.-Saldo +38.500 €.",
    icon: LineChart,
    accent: "leaf",
  },
  {
    id: "einnahmen",
    category: "Wirtschaftlichkeit",
    title: "Zusätzliche Einnahmepotenziale",
    description:
      "Wir prüfen Mieterstrom, Mieterwärme, Direktvermarktung, E-Mobilität und weitere Erlösmodelle.",
    benefit: "Aus einer Energieanlage kann ein zusätzliches Geschäftsmodell entstehen.",
    chips: ["Mieterstrom", "Mieterwärme", "Direktvermarktung", "THG-Quote", "E-Mobilität", "Regelenergie"],
    example: "Mieterstrom im MFH mit 6 Wohneinheiten erschließt zusätzliche Einnahmen.",
    icon: Coins,
    accent: "orange",
  },
  {
    id: "risiken",
    category: "Risiken",
    title: "Risiken & Zukunftssicherheit",
    description:
      "Wir bewerten technologische, regulatorische und wirtschaftliche Risiken sowie Veränderungen im Energiemarkt.",
    benefit: "Sie kennen nicht nur Chancen, sondern auch Schwachstellen und Risiken Ihres Projekts.",
    chips: ["EEG", "Strompreis", "Technologie", "Wiederverkauf", "Regulierung", "Ersatzinvest", "Zukunftsfähigkeit"],
    example: "Wegfall oder Änderung einer Vergütung beeinflusst die Rendite spürbar.",
    icon: ShieldAlert,
    accent: "navy",
  },
];

const accentStyles: Record<
  AnalysisModule["accent"],
  { iconBg: string; iconColor: string; numBg: string; numColor: string }
> = {
  navy: {
    iconBg: "bg-navy/8",
    iconColor: "text-navy",
    numBg: "bg-navy",
    numColor: "text-white",
  },
  leaf: {
    iconBg: "bg-[color-mix(in_oklab,var(--leaf)_15%,white)]",
    iconColor: "text-[var(--leaf-deep)]",
    numBg: "bg-[var(--leaf-deep)]",
    numColor: "text-white",
  },
  orange: {
    iconBg: "bg-orange/12",
    iconColor: "text-orange",
    numBg: "bg-orange",
    numColor: "text-white",
  },
};

export function AnalysisModulesGrid() {
  const categories = useMemo(
    () => ["Alle", ...Array.from(new Set(modules.map((m) => m.category)))],
    [],
  );
  const [active, setActive] = useState("Alle");

  const visible = active === "Alle" ? modules : modules.filter((m) => m.category === active);

  return (
    <section className="bg-white border-b border-[var(--rule)]">
      <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-16 md:py-24">
        {/* Header */}
        <div className="max-w-3xl mb-10 md:mb-14">
          <div className="eyebrow text-[var(--leaf-deep)] mb-3">12 Analysebereiche</div>
          <h2 className="font-display font-bold text-navy text-3xl md:text-4xl leading-tight">
            Was analysiert Energy Prime <span className="text-orange">360</span>?
          </h2>
          <p className="mt-5 text-navy/75 text-base md:text-lg leading-relaxed">
            Wir prüfen Ihr Energie-, Gebäude- und Investitionsprojekt ganzheitlich –
            technisch, wirtschaftlich, steuerlich und förderseitig. So entsteht eine klare
            Entscheidungsgrundlage statt einzelner Anbietermeinungen.
          </p>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-8 md:mb-10">
          {categories.map((cat) => {
            const isActive = cat === active;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setActive(cat)}
                className={`text-sm font-medium rounded-full px-4 py-2 border transition ${
                  isActive
                    ? "bg-navy text-white border-navy shadow-sm"
                    : "bg-white text-navy/75 border-[var(--rule)] hover:border-navy/40 hover:text-navy"
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 items-stretch">
          {visible.map((m) => {
            const idx = modules.findIndex((x) => x.id === m.id);
            const styles = accentStyles[m.accent];
            const Icon = m.icon;
            return (
              <article
                key={m.id}
                className="group bg-white rounded-2xl border border-[var(--rule)] shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 p-6 md:p-7 flex flex-col h-full"
              >
                {/* Top row: number + icon */}
                <div className="flex items-start justify-between">
                  <span
                    className={`inline-flex items-center justify-center text-xs font-bold rounded-full w-9 h-9 ${styles.numBg} ${styles.numColor}`}
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span
                    className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${styles.iconBg}`}
                  >
                    <Icon className={`w-6 h-6 ${styles.iconColor}`} strokeWidth={1.75} />
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-display font-bold text-navy text-lg md:text-[1.2rem] leading-snug mt-5">
                  {m.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-navy/70 leading-relaxed mt-2.5">
                  {m.description}
                </p>

                {/* Benefit */}
                <div className="mt-5 rounded-xl bg-[color-mix(in_oklab,var(--leaf)_10%,white)] border border-[color-mix(in_oklab,var(--leaf)_25%,white)] px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--leaf-deep)] mb-1">
                    Ihr Vorteil
                  </div>
                  <p className="text-sm text-navy/85 leading-snug">{m.benefit}</p>
                </div>

                {/* Chips */}
                <div className="mt-5">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-navy/55 mb-2">
                    Geprüfte Punkte
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {m.chips.map((c) => (
                      <span
                        key={c}
                        className="inline-flex items-center text-xs font-medium text-navy/80 bg-[#f4f6fa] border border-[var(--rule)] rounded-full px-2.5 py-1"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Example */}
                {m.example && (
                  <div className="mt-auto pt-5">
                    <div className="border-t border-dashed border-[var(--rule)] pt-3 flex items-start gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-orange shrink-0 mt-0.5">
                        Beispiel
                      </span>
                      <p className="text-xs text-navy/65 leading-snug italic">{m.example}</p>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        {/* Summary + CTA */}
        <div className="mt-12 md:mt-16 rounded-2xl bg-navy text-white px-6 md:px-10 py-8 md:py-10 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
          <p className="text-base md:text-lg leading-relaxed flex-1 text-white/90">
            Das Ergebnis ist keine Einzelmeinung, sondern eine vollständige, nachvollziehbare
            Gesamtbewertung Ihres Projekts – mit klarer Empfehlung, realistischen Zahlen und
            verständlichen nächsten Schritten.
          </p>
          <a
            href="/projekt-beschreiben"
            className="shrink-0 inline-flex items-center gap-2 bg-orange hover:bg-orange/90 text-white font-semibold px-6 py-3 rounded-md transition shadow-md"
          >
            Kostenlose Analyse starten
            <span aria-hidden>→</span>
          </a>
        </div>
      </div>
    </section>
  );
}
